type StorageLike = Pick<Storage, "getItem" | "removeItem" | "setItem">;

/**
 * Where a user's choice to keep the assistant open is kept: per user, so a second person signing
 * in on the same browser does not inherit the first one's open panel, and per browser, which is
 * the whole of its promise (#6128).
 */
export function assistantOpenStorageKey(userId: string): string {
  return `attorneyhub.assistant.open.v1:${userId}`;
}

/**
 * Whether this user left the assistant open.
 *
 * Absence means closed: a user who has never opened it meets the nav as it always was.
 */
export function loadAssistantOpen(userId: string | undefined, storage = getBrowserLocalStorage()): boolean {
  if (!userId || !storage) return false;

  try {
    return storage.getItem(assistantOpenStorageKey(userId)) === "true";
  } catch {
    return false;
  }
}

/** Remembers the choice, and removes the key again on close so it exists only while open. */
export function saveAssistantOpen(
  userId: string | undefined,
  open: boolean,
  storage = getBrowserLocalStorage(),
): void {
  if (!userId || !storage) return;

  try {
    if (open) {
      storage.setItem(assistantOpenStorageKey(userId), "true");
      return;
    }

    storage.removeItem(assistantOpenStorageKey(userId));
  } catch (error) {
    // Storage the browser refuses - a private window with it disabled, a quota already full. The
    // assistant still opens; it only does not stay open across a reload.
    console.warn("Could not remember whether the assistant is open.", error);
  }
}

/** Where a user's width for the docked assistant is kept, per user and per browser like the open choice (#7205). */
export function assistantWidthStorageKey(userId: string): string {
  return `attorneyhub.assistant.width.v1:${userId}`;
}

/**
 * The width this user dragged the assistant to, in pixels, or `undefined` for the default.
 *
 * Not clamped here: the room it has depends on the window it is drawn in, which the shell measures.
 */
export function loadAssistantWidth(userId: string | undefined, storage = getBrowserLocalStorage()): number | undefined {
  if (!userId || !storage) return undefined;

  let saved: string | null;
  try {
    saved = storage.getItem(assistantWidthStorageKey(userId));
  } catch {
    return undefined;
  }
  const width = Number(saved);
  if (saved === null || !Number.isFinite(width) || width <= 0) return undefined;
  return width;
}

/** Remembers the width, or forgets it again for `undefined`, so a reset leaves no key behind. */
export function saveAssistantWidth(
  userId: string | undefined,
  width: number | undefined,
  storage = getBrowserLocalStorage(),
): void {
  if (!userId || !storage) return;

  try {
    if (width !== undefined) {
      storage.setItem(assistantWidthStorageKey(userId), `${Math.round(width)}`);
      return;
    }

    storage.removeItem(assistantWidthStorageKey(userId));
  } catch (error) {
    // As for the open choice: the assistant still resizes, it only does not keep the width.
    console.warn("Could not remember the assistant's width.", error);
  }
}

function getBrowserLocalStorage(): StorageLike | undefined {
  try {
    return globalThis.localStorage;
  } catch {
    return undefined;
  }
}
